import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import { Tag } from "../components/atoms";
import { getQualityIssues, getQualityFacets, getQualityIssueSummary, updateQualityIssue } from "../services/api";
import { Globe, Link2, MapPin, Package, FileText, Users, Calendar, Hash, AlertTriangle, XCircle, Download, RefreshCw, Eye, Pencil, Check, X } from "lucide-react";
import { FilterSelect, FilterClear } from "../components/FilterBar";
import Pagination from "../components/Pagination";
import Refreshing from "../components/Refreshing";
import { useServerList, useDebounced } from "../hooks/useServerList";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

const ISSUE_TYPE_CFG = {
  missing_website:         { label:"Missing Website",          Icon:Globe,          color:"#C1121F", action:"Find and add the official website URL." },
  missing_linkedin:        { label:"Missing LinkedIn",         Icon:Link2,          color:"#E8431A", action:"Search LinkedIn and add the company page URL." },
  missing_country:         { label:"Missing Country",          Icon:MapPin,         color:"#B23A7E", action:"Check exhibitor directory and company website for HQ location." },
  missing_category:        { label:"Missing Product Category", Icon:Package,        color:"#F18F18", action:"Review exhibitor profile and assign product categories." },
  missing_contact:         { label:"Missing Contact",          Icon:FileText,       color:"#960659", action:"Find contact email or phone from the company website." },
  duplicate_candidate:     { label:"Duplicate Candidates",     Icon:Users,          color:"#960659", action:"Compare entries and merge or mark as distinct." },
  old_research_date:       { label:"Old Research Date",        Icon:Calendar,       color:"#960659", action:"Re-research company from the latest exhibitor list." },
  category_mapping_review: { label:"Category Mapping Review",  Icon:Hash,           color:"#5F1156", action:"Review category assignment against taxonomy." },
  needs_enrichment:        { label:"Needs Enrichment",         Icon:AlertTriangle,  color:"#9a5b00", action:"Trigger enrichment pipeline for this company." },
  processing_error:        { label:"Processing Error",         Icon:XCircle,        color:"#C1121F", action:"Check enrichment logs and re-run the pipeline." },
};

const STATUS_FROM_DB = { new:"New", in_progress:"In Progress", in_review:"In Review", resolved:"Resolved" };
const STATUS_TO_DB   = Object.fromEntries(Object.entries(STATUS_FROM_DB).map(([k, v]) => [v, k]));
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";

function normalizeIssue(r) {
  const cfg = ISSUE_TYPE_CFG[r.issue_type] || {};
  return {
    id:         r.issue_id,
    company:    r.company_name || "—",
    type:       cfg.label || r.issue_type || "—",
    typeKey:    r.issue_type,
    severity:   capitalize(r.severity),
    show:       r.show_name || "—",
    year:       r.year || "—",
    detected:   r.detected_at ? String(r.detected_at).slice(0,10) : "—",
    assignedTo: r.assigned_to_name || "Unassigned",
    status:     STATUS_FROM_DB[r.status] || capitalize(r.status) || "New",
    desc:       r.description || "—",
    action:     cfg.action || "Review and resolve this issue.",
  };
}

function sevColor(s)    { return s==="High"   ? ["#C1121F","#fdecec"] : s==="Medium" ? ["#9a5b00","#fef4e6"] : ["#818084","#F4F4F5"]; }
function statusColor(s) { return s==="New"    ? ["#E8431A","#FCEBE5"] : s==="In Progress" ? ["#9a5b00","#fef4e6"] : s==="Resolved" ? ["#16a34a","#dcfce7"] : ["#818084","#F4F4F5"]; }

export default function QualityPage() {
  const {
    selectedEvent, changeEvent, nav,
    currentRole, setCurrentRole,
    notifs, setNotifs, notifOpen, setNotifOpen,
  } = useApp();

  const [issues,   setIssues]   = useState([]);
  const [summary,  setSummary]  = useState([]);
  const [selIssue, setSelIssue] = useState(null);
  const [issueTab, setIssueTab] = useState("all");

  const [search,          setSearch]          = useState("");
  const [filterType,      setFilterType]      = useState("All");
  const [filterStatus,    setFilterStatus]    = useState("All");
  const [filterSeverity,  setFilterSeverity]  = useState("All");
  const [filterShow,      setFilterShow]      = useState("All");
  const [filterYear,      setFilterYear]      = useState(String(new Date().getFullYear()));
  const [filterAssigned,  setFilterAssigned]  = useState("All");

  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Data ────────────────────────────────────────────────────────────────────
  // Filters and paging happen in SQL. This page holds one page of issues, so the
  // dropdown options and the queue counts come from the API rather than from the
  // rows on screen.

  const debouncedSearch = useDebounced(search, 400);

  const query = React.useMemo(() => {
    const q = {};
    if (debouncedSearch)             q.search           = debouncedSearch;
    if (filterType     !== "All")    q.issue_type       = filterType;
    if (filterStatus   !== "All")    q.status           = STATUS_TO_DB[filterStatus] || filterStatus;
    if (filterSeverity !== "All")    q.severity         = filterSeverity.toLowerCase();
    if (filterShow     !== "All")    q.show_name        = filterShow;
    if (filterYear     !== "All")    q.year             = filterYear;
    if (filterAssigned !== "All")    q.assigned_to_name = filterAssigned;
    return q;
  }, [debouncedSearch, filterType, filterStatus, filterSeverity, filterShow, filterYear, filterAssigned]);

  const { items, total, offset, setOffset, loading, initialLoading, refresh } =
    useServerList(getQualityIssues, query, rowsPerPage);

  const pageRows = React.useMemo(() => items.map(normalizeIssue), [items]);
  useEffect(() => { setIssues(pageRows); }, [pageRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dropdown options depend on the other filters, so they refetch with the query.
  // The guard stops a slow reply from overwriting a newer one.
  const [facets, setFacets] = useState({});
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    let stale = false;
    getQualityFacets(query)
      .then((f) => { if (!stale) setFacets(f); })
      .catch(() => { if (!stale) setFacets({}); });
    return () => { stale = true; };
  }, [queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The queue totals across the top are deliberately unfiltered — they show what
  // is outstanding overall, not within the current view — so they are fetched once
  // rather than on every filter change.
  useEffect(() => {
    getQualityIssueSummary()
      .then((s) => setSummary(Array.isArray(s) ? s : []))
      .catch(() => setSummary([]));
  }, []);

  const queueCounts = {};
  summary.forEach(r => {
    queueCounts[r.issue_type] = (queueCounts[r.issue_type] || 0) + (r.open_count || 0);
  });

  const uniqueShows    = facets.show || [];
  const uniqueYears    = facets.year || [];
  const uniqueAssigned = facets.assigned || [];
  const uniqueTypes    = Object.keys(ISSUE_TYPE_CFG);

  const resolveIssue = (id, e) => {
    e.stopPropagation();
    updateQualityIssue(id, { status: "resolved" }).catch(() => {});
    setIssues(prev => prev.map(i => i.id === id ? { ...i, status: "Resolved" } : i));
    if (selIssue === id) setSelIssue(null);
  };

  const clearFilters = () => {
    setSearch(""); setFilterType("All"); setFilterStatus("All");
    setFilterSeverity("All"); setFilterShow("All"); setFilterYear("All");
    setFilterAssigned("All"); 
  };

  const selIss = selIssue ? issues.find(i => i.id === selIssue) : null;




  return (
    <PageShell>
      <style>{`*::-webkit-scrollbar{width:5px}*::-webkit-scrollbar-thumb{background:#D8DADC;border-radius:3px}table.ct{width:100%;border-collapse:collapse;font-size:11px}table.ct th{text-align:left;padding:8px 10px;font-size:9px;font-weight:700;color:#818084;text-transform:uppercase;letter-spacing:0.4px;border-bottom:2px solid #D8DADC;background:#FAFAFA}table.ct td{padding:8px 10px;border-bottom:1px solid #F4F4F5}`}</style>

      <Sidebar active="quality" onNav={nav}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}/>

      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 28px",background:"#fff",borderBottom:"1px solid #D8DADC"}}>
          <div>
            <div style={{fontSize:20,fontWeight:800}}>Data Quality</div>
            <div style={{fontSize:11,color:"#818084",marginTop:2}}>Fix missing, duplicate, outdated, or blocked records before they are used to improve accuracy and reliability.</div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button style={{padding:"7px 12px",borderRadius:7,border:"1px solid #D8DADC",background:"#fff",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46",display:"flex",alignItems:"center",gap:4}}><Download size={14}/> Export Issues</button>
            <button onClick={refresh} style={{padding:"7px 12px",borderRadius:7,border:"1px solid #D8DADC",background:"#fff",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46",display:"flex",alignItems:"center",gap:4}}><RefreshCw size={14}/> Refresh</button>
          </div>
        </div>

        <div style={{flex:1,overflow:"auto",padding:"16px 28px 32px"}}>

          <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
            <FilterSelect value={filterShow} onChange={e=>{setFilterShow(e.target.value);}} active={filterShow !== "All"}>
              <option value="All">Competitor Show: All</option>
              {uniqueShows.map(s=><option key={s}>{s}</option>)}
            </FilterSelect>
            <FilterSelect value={filterYear} onChange={e=>{setFilterYear(e.target.value);}} active={filterYear !== "All"}>
              <option value="All">Year: All</option>
              {uniqueYears.map(y=><option key={y}>{y}</option>)}
            </FilterSelect>
            <FilterSelect value={filterAssigned} onChange={e=>{setFilterAssigned(e.target.value);}} active={filterAssigned !== "All"}>
              <option value="All">Assigned To: All</option>
              {uniqueAssigned.map(a=><option key={a}>{a}</option>)}
            </FilterSelect>
            <FilterSelect value={filterSeverity} onChange={e=>{setFilterSeverity(e.target.value);}} active={filterSeverity !== "All"}>
              <option value="All">Severity: All</option>
              {["High","Medium","Low"].map(s=><option key={s}>{s}</option>)}
            </FilterSelect>
          </div>

          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>Issue Queues</div>
            {summary.length === 0 && initialLoading
              ? <div style={{textAlign:"center",padding:"32px",color:"#818084",fontSize:12}}>Loading queues…</div>
              : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
                {Object.entries(ISSUE_TYPE_CFG).map(([key,cfg]) => {
                  const count = queueCounts[key] || 0;
                  return (
                    <div key={key}
                      onClick={()=>{setFilterType(key===filterType?"All":key);}}
                      style={{background:"#fff",borderRadius:10,border:`1px solid ${filterType===key?cfg.color:"#D8DADC"}`,padding:"14px 12px",textAlign:"center",cursor:"pointer",transition:"border-color 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=cfg.color}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=filterType===key?cfg.color:"#D8DADC"}>
                      <div style={{marginBottom:4,display:"flex",justifyContent:"center"}}>{cfg.Icon && <cfg.Icon size={18} color={cfg.color}/>}</div>
                      <div style={{fontSize:9,color:"#818084",fontWeight:500,lineHeight:1.3,marginBottom:4,minHeight:24}}>{cfg.label}</div>
                      <div style={{fontSize:22,fontWeight:800,fontFamily:M,color:cfg.color}}>{count.toLocaleString()}</div>
                      <div style={{fontSize:9,color:"#E8431A",marginTop:4}}>View all</div>
                    </div>
                  );
                })}
              </div>
            }
          </div>

          <div style={{display:"flex",gap:0}}>
            <div style={{flex:1,overflow:"auto"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <span style={{fontSize:14,fontWeight:700}}>Issues List</span>
                <div style={{display:"flex",gap:0,marginLeft:8}}>
                  {[{id:"all",l:"All Issues"},{id:"my",l:"My Issues"}].map(t => (
                    <div key={t.id} onClick={()=>setIssueTab(t.id)}
                      style={{padding:"5px 12px",fontSize:10,fontWeight:issueTab===t.id?700:400,color:issueTab===t.id?"#E8431A":"#818084",borderBottom:issueTab===t.id?"2px solid #E8431A":"2px solid #D8DADC",cursor:"pointer"}}>
                      {t.l}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
                <input value={search} onChange={e=>{setSearch(e.target.value);}}
                  placeholder="Search company name..."
                  style={{padding:"7px 12px",borderRadius:8,border:"1px solid #D8DADC",fontSize:11,fontFamily:F,outline:"none",width:200}}/>
                <FilterSelect value={filterType} onChange={e=>{setFilterType(e.target.value);}} active={filterType !== "All"}>
                  <option value="All">Issue Type: All</option>
                  {uniqueTypes.map(k=><option key={k} value={k}>{ISSUE_TYPE_CFG[k].label}</option>)}
                </FilterSelect>
                <FilterSelect value={filterStatus} onChange={e=>{setFilterStatus(e.target.value);}} active={filterStatus !== "All"}>
                  <option value="All">Status: All</option>
                  {["New","In Progress","In Review","Resolved"].map(s=><option key={s}>{s}</option>)}
                </FilterSelect>
                <FilterSelect value={filterSeverity} onChange={e=>{setFilterSeverity(e.target.value);}} active={filterSeverity !== "All"}>
                  <option value="All">Severity: All</option>
                  {["High","Medium","Low"].map(s=><option key={s}>{s}</option>)}
                </FilterSelect>
                <FilterSelect value={filterAssigned} onChange={e=>{setFilterAssigned(e.target.value);}} active={filterAssigned !== "All"}>
                  <option value="All">Assigned To: All</option>
                  {uniqueAssigned.map(a=><option key={a}>{a}</option>)}
                </FilterSelect>
                <FilterClear onClick={clearFilters} />
              </div>

              {initialLoading
                ? <div style={{textAlign:"center",padding:"48px",color:"#818084",fontSize:12}}>Loading issues…</div>
                : <Refreshing active={loading}>
                <table className="ct">
                  <thead>
                    <tr>
                      <th style={{width:20}}><input type="checkbox" style={{accentColor:"#E8431A"}}/></th>
                      <th>Company</th><th>Issue Type</th><th>Severity</th>
                      <th>Competitor Show</th><th>Year</th><th>Detected On</th>
                      <th>Assigned To</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length===0 && (
                      <tr><td colSpan={10} style={{textAlign:"center",padding:32,color:"#818084"}}>No issues match the current filters.</td></tr>
                    )}
                    {pageRows.map(iss => {
                      const [sc, sbg] = sevColor(iss.severity);
                      const [stc, stbg] = statusColor(iss.status);
                      const cfg = ISSUE_TYPE_CFG[iss.typeKey] || {};
                      return (
                        <tr key={iss.id}
                          style={{background:selIssue===iss.id?"#FCEBE5":"transparent",cursor:"pointer"}}
                          onClick={()=>setSelIssue(iss.id===selIssue?null:iss.id)}>
                          <td><input type="checkbox" style={{accentColor:"#E8431A"}} onClick={e=>e.stopPropagation()}/></td>
                          <td style={{fontWeight:600,color:"#E8431A"}}>{iss.company}</td>
                          <td>
                            <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10}}>
                              {cfg.Icon && <cfg.Icon size={14} color={cfg.color}/>}{iss.type}
                            </div>
                          </td>
                          <td><Tag label={iss.severity} c={sc} bg={sbg}/></td>
                          <td style={{fontSize:10}}>{iss.show}</td>
                          <td style={{fontFamily:M,fontSize:10}}>{iss.year}</td>
                          <td style={{fontSize:10,color:"#818084"}}>{iss.detected}</td>
                          <td style={{fontSize:10}}>{iss.assignedTo}</td>
                          <td><Tag label={iss.status} c={stc} bg={stbg}/></td>
                          <td>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              <span style={{cursor:"pointer"}} title="View" onClick={e=>{e.stopPropagation();setSelIssue(iss.id);}}><Eye size={14}/></span>
                              <span style={{cursor:"pointer"}} title="Edit"><Pencil size={14}/></span>
                              <span style={{cursor:"pointer",color:"#16a34a"}} title="Resolve" onClick={e=>resolveIssue(iss.id,e)}><Check size={14}/></span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginTop:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:"#818084",fontFamily:F}}>
                    Rows per page
                    <select value={rowsPerPage} onChange={e=>setRowsPerPage(Number(e.target.value))}
                      style={{padding:"3px 6px",borderRadius:4,border:"1px solid #D8DADC",fontSize:10,fontFamily:F}}>
                      <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                    </select>
                  </div>
                  <div style={{flex:1}}>
                    <Pagination total={total} offset={offset} limit={rowsPerPage}
                                onChange={setOffset} disabled={loading} />
                  </div>
                </div>
                </Refreshing>
              }
            </div>

            {selIss && (
              <div style={{width:340,flexShrink:0,background:"#fff",borderLeft:"1px solid #D8DADC",padding:"16px",overflow:"auto",marginLeft:8,borderRadius:"0 10px 10px 0"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:12}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:700}}>{selIss.company}</div>
                    <div style={{display:"flex",gap:4,marginTop:4}}>
                      <Tag label={selIss.type}     c={sevColor(selIss.severity)[0]} bg={sevColor(selIss.severity)[1]}/>
                      <Tag label={selIss.severity} c={sevColor(selIss.severity)[0]} bg={sevColor(selIss.severity)[1]}/>
                    </div>
                    <div style={{fontSize:9,color:"#818084",marginTop:4}}>ID: {selIss.id}</div>
                  </div>
                  <span onClick={()=>setSelIssue(null)} style={{cursor:"pointer",color:"#818084",display:"flex",alignItems:"center"}}><X size={16}/></span>
                </div>

                <div style={{display:"flex",gap:4,marginBottom:12}}>
                  {["Issue Details","Company Info","History","Notes","Citations"].map((t,i) => (
                    <span key={t} style={{padding:"3px 8px",borderRadius:4,fontSize:9,fontWeight:i===0?700:400,color:i===0?"#E8431A":"#818084",background:i===0?"#FCEBE5":"transparent",cursor:"pointer"}}>{t}</span>
                  ))}
                </div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 16px",fontSize:10,marginBottom:16}}>
                  {[["Company",selIss.company],["Competitor Show",selIss.show],["Year",selIss.year],["Detected On",selIss.detected],["Issue Type",selIss.type],["Severity",selIss.severity],["Assigned To",selIss.assignedTo],["Status",selIss.status]].map(([l,v]) => (
                    <div key={l}>
                      <div style={{color:"#818084",fontSize:9,marginBottom:2}}>{l}</div>
                      <div style={{fontWeight:600}}>{v}</div>
                    </div>
                  ))}
                </div>

                <div style={{marginBottom:12}}>
                  <div style={{fontSize:9,color:"#818084",marginBottom:3}}>Description</div>
                  <div style={{fontSize:11,color:"#3F3F46",lineHeight:1.5}}>{selIss.desc}</div>
                </div>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:9,color:"#818084",marginBottom:3}}>Recommended Action</div>
                  <div style={{fontSize:11,color:"#3F3F46",lineHeight:1.5}}>{selIss.action}</div>
                </div>

                <div style={{borderTop:"1px solid #D8DADC",paddingTop:14}}>
                  <div style={{fontSize:12,fontWeight:700,marginBottom:10}}>Update Issue</div>
                  {selIss.typeKey==="missing_website" && (
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:600,marginBottom:4}}>Add Website URL</div>
                      <input placeholder="https://www.example.com"
                        style={{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid #D8DADC",fontSize:11,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                  )}
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:10,fontWeight:600,marginBottom:4}}>Add Citation / Source</div>
                    <div style={{display:"flex",gap:4}}>
                      <input placeholder="Enter source URL or reference"
                        style={{flex:1,padding:"8px 10px",borderRadius:6,border:"1px solid #D8DADC",fontSize:11,fontFamily:F,outline:"none"}}/>
                      <button style={{padding:"8px 12px",borderRadius:6,border:"1px solid #D8DADC",background:"#fff",fontSize:14,cursor:"pointer"}}>+</button>
                    </div>
                  </div>
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:10,fontWeight:600,marginBottom:4}}>Notes</div>
                    <textarea placeholder="Add notes..." rows={3}
                      style={{width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid #D8DADC",fontSize:11,fontFamily:F,outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={e=>resolveIssue(selIss.id,e)}
                      style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:"#E8431A",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Check size={12}/> Mark Reviewed</button>
                    <button style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:"#E8431A",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F}}>Save Changes</button>
                    <button onClick={()=>setSelIssue(null)} style={{padding:"7px 10px",borderRadius:6,border:"1px solid #D8DADC",background:"#fff",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46"}}>Close</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
